import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, FileText, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useBankAusIban, useOrtAusPlz, OrtVorschlaege, BankHinweis } from '@/lib/stammdaten';
import api, { membershipsAPI } from '@/lib/api';

/* Papiervertrag per Foto (Betreiber 28.09.2026): Seiten fotografieren, Daten vom
   Papier übernehmen, als kompletten Vertrag ablegen — auch für ein neues Mitglied
   mit neuer Mitgliedschaft und SEPA-Mandat aus der Papier-IBAN. Die Fotos werden
   ein PDF, verschlüsselt am Mitglied. Laufende Verträge im CRM werden nie still
   geändert: Abweichungen landen als Aufgabe beim Inhaber. */
const MAX_KANTE = 2400;
const LEER_PERSON = { vorname: '', nachname: '', geburtsdatum: '', strasse: '', plz: '', ort: '', telefon: '', email: '',
  erziehungsberechtigter_name: '', erziehungsberechtigter_telefon: '', erziehungsberechtigter_email: '' };

function verkleinern(datei) {
  if (datei.type === 'application/pdf') return Promise.resolve(datei);
  return new Promise((resolve) => {
    const url = URL.createObjectURL(datei);
    const img = new Image();
    img.onload = () => {
      const f = Math.min(1, MAX_KANTE / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement('canvas');
      c.width = Math.round(img.naturalWidth * f); c.height = Math.round(img.naturalHeight * f);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      c.toBlob(b => { URL.revokeObjectURL(url); resolve(b ? new File([b], (datei.name || 'seite').replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : datei); }, 'image/jpeg', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(datei); };   // Server versucht es selbst
    img.src = url;
  });
}

export function ibanOk(roh) {
  const s = String(roh || '').replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(s)) return false;
  const z = (s.slice(4) + s.slice(0, 4)).replace(/[A-Z]/g, ch => String(ch.charCodeAt(0) - 55));
  let rest = 0;
  for (let i = 0; i < z.length; i += 7) rest = Number(String(rest) + z.slice(i, i + 7)) % 97;
  return rest === 1;
}

const Feld = ({ label, children, breit }) => (
  <label className={`space-y-1 text-xs ${breit ? 'col-span-2' : ''}`}><span className="text-muted-foreground">{label}</span>{children}</label>
);

export default function PapiervertragFotoDialog({ leadId = null, mitgliedschaft = null, offen, onClose, onFertig }) {
  const kamera = useRef(null);
  const [seiten, setSeiten] = useState([]);
  const [person, setPerson] = useState(LEER_PERSON);
  const [ziel, setZiel] = useState(null);          // {id, name} bestehendes Mitglied
  const [neu, setNeu] = useState(false);
  const [ms, setMs] = useState(null);              // laufende Mitgliedschaft des Ziels
  const [plaene, setPlaene] = useState([]);
  const [vertrag, setVertrag] = useState({ plan_id: '', beginn: '', laufzeit: '', beitrag: '' });
  const [bank, setBank] = useState({ kontoinhaber: '', iban: '', bic: '', datum_unterschrift: '' });
  const bankErkannt = useBankAusIban(bank.iban, b => setBank(x => ({ ...x, bic: b.bic })));
  const ortListe = useOrtAusPlz(person.plz, person.ort, v => setPerson(x => ({ ...x, ort: v })));
  const [haken, setHaken] = useState({ unterschrift: false, sepa: false });
  const [bemerkung, setBemerkung] = useState('');
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);

  useEffect(() => {
    if (!offen) return;
    setSeiten([]); setErgebnis(null); setPerson(LEER_PERSON); setBank({ kontoinhaber: '', iban: '', bic: '', datum_unterschrift: '' });
    setHaken({ unterschrift: false, sepa: false }); setBemerkung(''); setSuche(''); setTreffer([]); setNeu(false);
    setZiel(leadId ? { id: leadId } : null); setMs(mitgliedschaft || null);
    api.get('/memberships/plans').then(r => setPlaene(Array.isArray(r.data) ? r.data : (r.data.plans || []))).catch(() => setPlaene([]));
  }, [offen]); // eslint-disable-line react-hooks/exhaustive-deps -- nur beim Öffnen zurücksetzen, nicht nach dem Neuladen des Mitglieds

  // Laufende Mitgliedschaft des gewählten Mitglieds
  // Antworten zu einem vorher gewählten Mitglied verwerfen (30.09.2026: nach Domenic
  // landete dessen Mitgliedschaft im leeren Dialog fürs nächste, neue Mitglied)
  useEffect(() => {
    if (!offen) return undefined;
    if (!ziel?.id) { if (!leadId) setMs(null); return undefined; }
    if (mitgliedschaft && ziel.id === leadId) return undefined;
    let aktuell = true;
    membershipsAPI.member(ziel.id).then(r => { if (aktuell) setMs(r.data.active || null); }).catch(() => { if (aktuell) setMs(null); });
    return () => { aktuell = false; };
  }, [offen, ziel]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (ms) setVertrag({ plan_id: '', beginn: (ms.start_date || '').slice(0, 10), laufzeit: ms.duration_months ? String(ms.duration_months) : '',
      beitrag: ms.monthly_price != null ? String(ms.monthly_price).replace('.', ',') : '' });
    else setVertrag({ plan_id: '', beginn: '', laufzeit: '', beitrag: '' });
  }, [ms]);

  useEffect(() => {
    if (leadId || suche.trim().length < 2) { setTreffer([]); return; }
    const h = setTimeout(async () => {
      try { setTreffer((await api.get('/leads', { params: { search: suche.trim(), limit: 6 } })).data.leads || []); } catch { setTreffer([]); }
    }, 250);
    return () => clearTimeout(h);
  }, [suche, leadId]);

  const planWaehlen = (id) => {
    const p = plaene.find(x => x.id === id);
    setVertrag(v => ({ ...v, plan_id: id, laufzeit: p ? String(p.duration_months || '') : v.laufzeit, beitrag: p ? String(p.monthly_price).replace('.', ',') : v.beitrag }));
  };
  const hinzu = async (e) => {
    const dateien = Array.from(e.target.files || []);
    e.target.value = '';
    if (!dateien.length) return;
    const neuS = await Promise.all(dateien.map(verkleinern));
    setSeiten(s => [...s, ...neuS.map(d => ({ datei: d, vorschau: d.type.startsWith('image/') ? URL.createObjectURL(d) : null }))].slice(0, 12));
  };

  const ibanFehler = bank.iban.trim() && !ibanOk(bank.iban);
  const bereit = seiten.length && haken.unterschrift && (ziel || (neu && person.vorname.trim() && person.nachname.trim()))
    && (ms || (vertrag.plan_id && vertrag.beginn)) && !ibanFehler;

  const speichern = async () => {
    if (!bereit) { toast.error('Fotos, Unterschrift, Mitglied und – ohne laufende Mitgliedschaft – Tarif und Beginn angeben'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      if (ziel?.id) fd.append('lead_id', ziel.id); else fd.append('neu_anlegen', 'true');
      if (ms?.id && ziel?.id) fd.append('membership_id', ms.id);   // nie bei „Neues Mitglied“
      fd.append('unterschrift_auf_papier', 'true');
      fd.append('sepa_auf_papier', haken.sepa ? 'true' : 'false');
      Object.entries(person).forEach(([k, v]) => { if (String(v).trim()) fd.append(k, String(v).trim()); });
      if (!ms && vertrag.plan_id) fd.append('plan_id', vertrag.plan_id);
      if (vertrag.beginn) fd.append('vertragsbeginn_papier', vertrag.beginn);
      if (vertrag.laufzeit !== '') fd.append('laufzeit_papier_monate', String(parseInt(vertrag.laufzeit, 10)));
      if (vertrag.beitrag !== '') fd.append('beitrag_papier', String(Number(String(vertrag.beitrag).replace(',', '.'))));
      Object.entries(bank).forEach(([k, v]) => { if (String(v).trim()) fd.append(k, String(v).trim()); });
      if (bemerkung.trim()) fd.append('bemerkung', bemerkung.trim());
      seiten.forEach(s => fd.append('seiten', s.datei));
      const r = await api.post('/vertraege/bestand/papierfoto', fd);
      setErgebnis(r.data); toast.success(`Vertrag angelegt (${r.data.vertrag.vertragsnummer})`);
      onFertig?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setBusy(false);
  };

  if (!offen) return null;
  const sp = (k, v) => setPerson(p => ({ ...p, [k]: v }));
  const sb = (k, v) => setBank(b => ({ ...b, [k]: v }));
  const sv = (k, v) => setVertrag(x => ({ ...x, [k]: v }));
  const kinderPlan = (plaene.find(p => p.id === vertrag.plan_id) || {}).category === 'kinder';

  return (
    <Dialog open={offen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Papiervertrag fotografieren</DialogTitle></DialogHeader>
        {ergebnis ? (
          <div className="space-y-3 text-sm" data-testid="papierfoto-ergebnis">
            <p><b>{ergebnis.vertrag.vertragsnummer}</b> ist angelegt ({ergebnis.vertrag.plan_name}, ab {ergebnis.vertrag.vertragsbeginn}).</p>
            <ul className="text-xs text-muted-foreground list-disc ml-4">
              {ergebnis.neues_mitglied && <li>Neues Mitglied angelegt.</li>}
              {ergebnis.neue_mitgliedschaft && <li>Neue Mitgliedschaft laut Papier angelegt.</li>}
              {ergebnis.mandat?.neu && <li>SEPA-Mandat {ergebnis.mandat.referenz} aus der Papier-IBAN angelegt.</li>}
              {ergebnis.mandat?.papier_verknuepft && !ergebnis.mandat?.neu && <li>Papier-Mandat mit SEPA-Mandat {ergebnis.mandat.referenz} verknüpft.</li>}
              {!ergebnis.mandat?.vorhanden && <li className="text-amber-700">Kein SEPA-Mandat – dafür ist eine Aufgabe angelegt.</li>}
            </ul>
            {ergebnis.abweichungen?.length > 0 && (
              <div className="rounded-md border border-amber-500 bg-amber-500/5 p-2 text-xs"><b>Abweichungen – CRM nicht geändert, Aufgabe für den Inhaber angelegt:</b>
                <ul className="list-disc ml-4 mt-1">{ergebnis.abweichungen.map(a => <li key={a}>{a}</li>)}</ul></div>
            )}
            <div className="flex gap-2">
              {!leadId && <Button size="sm" variant="outline" onClick={() => window.open(`/leads/${ergebnis.lead_id}`, '_self')}>Zum Mitglied</Button>}
              <Button size="sm" onClick={onClose}>Fertig</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <p className="text-xs text-muted-foreground">Jede Seite einzeln fotografieren, auch AGB- und SEPA-Seite. Die Seiten werden ein PDF und liegen verschlüsselt beim Mitglied.</p>
            <input ref={kamera} type="file" accept="image/*,application/pdf" capture="environment" multiple className="hidden" onChange={hinzu} data-testid="papierfoto-datei" />
            <div className="grid grid-cols-4 gap-2">
              {seiten.map((s, i) => (
                <div key={i} className="relative rounded-md border border-border overflow-hidden aspect-[3/4] bg-muted flex items-center justify-center">
                  {s.vorschau ? <img src={s.vorschau} alt={`Seite ${i + 1}`} className="object-cover w-full h-full" /> : <FileText className="w-6 h-6 text-muted-foreground" />}
                  <span className="absolute left-1 top-1 rounded bg-black/70 px-1 text-[10px] text-white">{i + 1}</span>
                  <button type="button" onClick={() => setSeiten(x => x.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5 text-white" aria-label={`Seite ${i + 1} entfernen`}><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button type="button" onClick={() => kamera.current?.click()} className="rounded-md border-2 border-dashed border-border aspect-[3/4] flex flex-col items-center justify-center gap-1 text-xs text-muted-foreground hover:bg-muted" data-testid="papierfoto-kamera">
                <Camera className="w-6 h-6" />{seiten.length ? 'Weitere Seite' : 'Seite 1'}
              </button>
            </div>

            {!leadId && (
              <section className="space-y-2">
                <p className="text-xs font-semibold">Mitglied</p>
                {ziel ? (
                  <div className="flex items-center gap-2 text-xs"><span>{ziel.name}{ms ? ` · ${ms.plan_name}` : ' · keine laufende Mitgliedschaft'}</span>
                    <button type="button" className="text-muted-foreground underline" onClick={() => { setZiel(null); setMs(null); }}>ändern</button></div>
                ) : (
                  <>
                    <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" className="accent-marke" checked={neu} onChange={e => setNeu(e.target.checked)} /> Neues Mitglied anlegen</label>
                    {!neu && <Input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Bestehendes Mitglied suchen (Name, E-Mail, Telefon)" className="h-8" />}
                    {!neu && treffer.length > 0 && <div className="flex flex-wrap gap-1.5">{treffer.map(t => <button key={t.id} type="button" onClick={() => { setZiel({ id: t.id, name: t.name }); setSuche(''); }} className="rounded-full border border-border px-2.5 py-1 text-xs hover:bg-muted">{t.name}</button>)}</div>}
                  </>
                )}
              </section>
            )}

            <section className="space-y-2">
              <p className="text-xs font-semibold">Person laut Papier {ziel && <span className="font-normal text-muted-foreground">– leere CRM-Felder werden ergänzt, Abweichendes wird Aufgabe</span>}</p>
              <div className="grid grid-cols-2 gap-2">
                <Feld label="Vorname"><Input value={person.vorname} onChange={e => sp('vorname', e.target.value)} className="h-8" /></Feld>
                <Feld label="Nachname"><Input value={person.nachname} onChange={e => sp('nachname', e.target.value)} className="h-8" /></Feld>
                <Feld label="Geburtsdatum"><Input type="date" value={person.geburtsdatum} onChange={e => sp('geburtsdatum', e.target.value)} className="h-8" /></Feld>
                <Feld label="Telefon"><Input value={person.telefon} onChange={e => sp('telefon', e.target.value)} className="h-8" /></Feld>
                <Feld label="Straße, Nr."><Input value={person.strasse} onChange={e => sp('strasse', e.target.value)} className="h-8" /></Feld>
                <div className="grid grid-cols-[80px_1fr] gap-2">
                  <Feld label="PLZ"><Input value={person.plz} onChange={e => sp('plz', e.target.value)} className="h-8" /></Feld>
                  <Feld label="Ort"><Input value={person.ort} onChange={e => sp('ort', e.target.value)} className="h-8" /><OrtVorschlaege orte={ortListe} aktuell={person.ort} wahl={v => sp('ort', v)} /></Feld>
                </div>
                <Feld label="E-Mail" breit><Input type="email" value={person.email} onChange={e => sp('email', e.target.value)} className="h-8" /></Feld>
              </div>
              {(kinderPlan || ms?.category === 'kinder' || person.erziehungsberechtigter_name) && (
                <div className="grid grid-cols-3 gap-2">
                  <Feld label="Erziehungsberechtigte/r"><Input value={person.erziehungsberechtigter_name} onChange={e => sp('erziehungsberechtigter_name', e.target.value)} className="h-8" /></Feld>
                  <Feld label="Telefon Eltern"><Input value={person.erziehungsberechtigter_telefon} onChange={e => sp('erziehungsberechtigter_telefon', e.target.value)} className="h-8" /></Feld>
                  <Feld label="E-Mail Eltern"><Input value={person.erziehungsberechtigter_email} onChange={e => sp('erziehungsberechtigter_email', e.target.value)} className="h-8" /></Feld>
                </div>
              )}
            </section>

            <section className="space-y-2 rounded-md border border-border/60 p-3">
              {ms ? (
                <p className="text-xs"><b>Vertrag laut Papier</b> – mit dem Foto vergleichen. Die laufende Mitgliedschaft ({ms.plan_name}) bleibt, wie sie ist; Abweichungen werden Aufgabe.</p>
              ) : (
                <>
                  <p className="text-xs"><b>Neue Mitgliedschaft laut Papier</b></p>
                  <select value={vertrag.plan_id} onChange={e => planWaehlen(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="papierfoto-tarif">
                    <option value="">Tarif wählen …</option>
                    {plaene.map(p => <option key={p.id} value={p.id}>{p.name} · {p.duration_label} · {Number(p.monthly_price).toFixed(2).replace('.', ',')} €</option>)}
                  </select>
                </>
              )}
              <div className="grid grid-cols-3 gap-2">
                <Feld label="Beginn"><Input type="date" value={vertrag.beginn} onChange={e => sv('beginn', e.target.value)} className="h-8" /></Feld>
                <Feld label="Laufzeit (Monate)"><Input inputMode="numeric" value={vertrag.laufzeit} onChange={e => sv('laufzeit', e.target.value.replace(/\D/g, ''))} className="h-8" /></Feld>
                <Feld label="Beitrag €/Monat"><Input inputMode="decimal" value={vertrag.beitrag} onChange={e => sv('beitrag', e.target.value)} className="h-8" /></Feld>
              </div>
            </section>

            <section className="space-y-2">
              <p className="text-xs font-semibold">SEPA laut Papier <span className="font-normal text-muted-foreground">– liegt schon ein Mandat vor, bleibt es; nur ohne Mandat entsteht eins aus dieser IBAN</span></p>
              <div className="grid grid-cols-2 gap-2">
                <Feld label="Kontoinhaber"><Input value={bank.kontoinhaber} onChange={e => sb('kontoinhaber', e.target.value)} className="h-8" /></Feld>
                <Feld label="Unterschrieben am"><Input type="date" value={bank.datum_unterschrift} onChange={e => sb('datum_unterschrift', e.target.value)} className="h-8" /></Feld>
                <Feld label="IBAN"><Input value={bank.iban} onChange={e => sb('iban', e.target.value.toUpperCase())} className={`h-8 font-mono ${ibanFehler ? 'border-red-500' : ''}`} /></Feld>
                <Feld label="BIC (optional)"><Input value={bank.bic} onChange={e => sb('bic', e.target.value.toUpperCase())} className="h-8 font-mono" /><BankHinweis bank={bankErkannt} /></Feld>
              </div>
              {ibanFehler && <p className="text-xs text-red-600">Prüfziffer passt nicht – IBAN Zeichen für Zeichen mit dem Foto vergleichen.</p>}
            </section>

            <div className="space-y-1.5">
              <label className="flex items-start gap-2 text-xs cursor-pointer"><input type="checkbox" className="mt-0.5 accent-marke" checked={haken.unterschrift} onChange={e => setHaken(h => ({ ...h, unterschrift: e.target.checked }))} /> Der Papiervertrag ist vom Mitglied (bei Kindern von den Eltern) unterschrieben.</label>
              <label className="flex items-start gap-2 text-xs cursor-pointer"><input type="checkbox" className="mt-0.5 accent-marke" checked={haken.sepa} onChange={e => setHaken(h => ({ ...h, sepa: e.target.checked }))} /> Das SEPA-Lastschriftmandat auf dem Papier ist unterschrieben.</label>
            </div>
            <Input value={bemerkung} onChange={e => setBemerkung(e.target.value)} placeholder="Bemerkung (optional)" className="h-8 text-xs" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={onClose}>Abbrechen</Button>
              <Button size="sm" onClick={speichern} disabled={busy || !bereit} className="gap-1.5" data-testid="papierfoto-speichern">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Vertrag anlegen
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
